import { useRef } from 'react';
import { FormApi } from 'informed';
import { useReduxState } from 'hooks';
import { Button, FlexContainer } from 'components/common';
import {
  ArrayField,
  Checkbox,
  Domains,
  FormGroup,
  FormSection,
  NumberStepper,
  RadioGroup,
  Select,
  Text,
  TextArea
} from 'components/common/form';
import { SiteSettingsForm } from '.';

import type { FormState } from 'informed';

interface Props {
  isVuoadmin?: boolean;
}

const SiteSettingsAccount = ({ isVuoadmin }: Props) => {
  const formApi = useRef<FormApi>(undefined);

  const { messages, serverConfig } = useReduxState();

  const handleAccountAskForConsentChange = (value) => {
    if (!value) {
      formApi.current?.setValue('accountAskForIsEmployee', false);
    }
  };

  const handleUsesUseNameChange = (userUseName) => {
    if (userUseName) {
      formApi.current?.setValue('userUseNameInCertificate', true);
    }
  };

  const handleAllowDeletionChange = (value) => {
    if (value) {
      formApi.current?.setValue('disableAccountDeleteRequests', false);
    }
  };

  const handleDisableAccountDeleteRequestsChange = (value: boolean) => {
    if (value) {
      formApi.current?.setValue('accountDeleteRequestEmailEnabled', false);
      formApi.current?.setValue('allowAdminToManageDeleteRequests', false);
    }
  };

  const updateValuesBeforeSubmit = async (values) => {
    if (isVuoadmin && !values.registerAdditionalTermsLinks) {
      values.registerAdditionalTermsLinks = [];
    }
    return values;
  };

  const {
    accountAllowDeletion,
    accountAnonymiseOnDeletion,
    accountAskForConsent,
    accountAskForIsEmployee,
    accountAskForTerms,
    accountDeleteRequestEmailContent,
    accountDeleteRequestEmailEnabled,
    accountDeleteRequestEmailRecipient,
    accountDeleteRequestEmailRecipientPreventDashboardUpdates,
    accountEnableUsernameAccounts,
    accountExpiration,
    accountExpirationIncludeAdmins,
    accountExpirationSendEmailWarning,
    accountExpirationSendEmailWarningDays,
    accountExpirationTimeInDays,
    accountExpirationDeleteBufferInDays,
    accountAddForceExpirationDate,
    accountAddForceExpirationDateMaxDays,
    accountGuestEnableHomePage,
    accountGuestUseName,
    accountLimitUsernameCreationToSuperadmins,
    accountPreventUserEditingProfileInfo,
    allowAdminToManageDeleteRequests,
    disableAccountDeleteRequests,
    enableRegistration,
    enableRegistrationType,
    registerAdditionalTermsLinks,
    registerEnableSettingsForSuperadmin,
    registerRestrictDomains,
    useAlternativeSetPassword,
    userUseName,
    userUseNameInCertificate
  } = serverConfig;

  const initialValues = {
    accountAllowDeletion,
    accountAnonymiseOnDeletion,
    accountAskForConsent,
    accountAskForIsEmployee,
    accountAskForTerms,
    accountDeleteRequestEmailContent,
    accountDeleteRequestEmailEnabled,
    accountDeleteRequestEmailRecipient,
    accountDeleteRequestEmailRecipientPreventDashboardUpdates,
    accountEnableUsernameAccounts,
    accountExpiration,
    accountExpirationIncludeAdmins,
    accountExpirationSendEmailWarning,
    accountExpirationSendEmailWarningDays,
    accountExpirationTimeInDays,
    accountExpirationDeleteBufferInDays,
    accountAddForceExpirationDate,
    accountAddForceExpirationDateMaxDays,
    accountGuestEnableHomePage,
    accountGuestUseName,
    accountLimitUsernameCreationToSuperadmins,
    accountPreventUserEditingProfileInfo,
    allowAdminToManageDeleteRequests,
    disableAccountDeleteRequests,
    enableRegistration,
    enableRegistrationType,
    registerAdditionalTermsLinks,
    registerEnableSettingsForSuperadmin,
    registerRestrictDomains,
    useAlternativeSetPassword,
    userUseName,
    userUseNameInCertificate
  };

  return (
    <div className="settings-account">
      <SiteSettingsForm
        initialValues={initialValues}
        padding={0}
        setFormApi={(informedApi) => (formApi.current = informedApi)}
        title={messages.siteSettingsAccountAndLogin}
        updateValuesBeforeSubmit={updateValuesBeforeSubmit}
      >
        {(formState: FormState<typeof initialValues>) => (
          <div>
            <FormSection title="Account and registration">
              {isVuoadmin ? (
                <>
                  <FormGroup label="Terms and consent">
                    <Checkbox
                      field="accountAskForConsent"
                      id="account-ask-for-consent"
                      label="Ask for consent when user creates account"
                      onValueChange={handleAccountAskForConsentChange}
                    />

                    <Checkbox
                      disabled={!formState.values?.accountAskForConsent}
                      field="accountAskForIsEmployee"
                      id="account-ask-for-is-employee"
                      label="Ask if user is in employment or contractual relationship with the training provider"
                      style={{ marginTop: 5 }}
                    />

                    <Checkbox
                      field="accountAskForTerms"
                      id="account-ask-for-terms"
                      label="Ask user to accept terms when user creates account"
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>

                  <FormGroup label="Additional terms links">
                    <ArrayField field="registerAdditionalTermsLinks">
                      {({ add }) => (
                        <>
                          <ArrayField.Items>
                            {({ remove, field }) => {
                              return (
                                <FlexContainer>
                                  <div style={{ marginRight: 10 }}>
                                    <Text
                                      field={`${field}.href`}
                                      placeholder="https://www.example.com/terms.pdf"
                                      style={{ width: 300 }}
                                    />
                                  </div>
                                  <div className="terms-link-text">
                                    <Text
                                      field={`${field}.text`}
                                      placeholder="Title"
                                    />
                                  </div>
                                  <div className="terms-link-actions">
                                    <Button
                                      aria-label={messages.commonDelete}
                                      category="light"
                                      data-tooltip={messages.commonDelete}
                                      icon="trash-alt"
                                      onClick={remove}
                                      size="small"
                                      style={{ marginLeft: 10 }}
                                    />
                                  </div>
                                </FlexContainer>
                              );
                            }}
                          </ArrayField.Items>

                          <Button
                            category="light"
                            icon="plus"
                            onClick={add}
                            size="small"
                            style={{ marginTop: 5 }}
                            text="Add link"
                          />
                        </>
                      )}
                    </ArrayField>
                  </FormGroup>

                  <FormGroup
                    label={messages.siteSettingsUserAccount}
                    style={{ marginTop: 25 }}
                  >
                    <RadioGroup
                      field="userUseName"
                      onChange={handleUsesUseNameChange}
                      options={[
                        {
                          id: `user-user-name-true`,
                          label: messages.siteSettingsUserUseName,
                          value: true
                        },
                        {
                          id: `user-use-name`,
                          label: messages.siteSettingsUserUseUsername,
                          value: false
                        }
                      ]}
                    />
                    <Checkbox
                      disabled={formState.values.userUseName}
                      field="userUseNameInCertificate"
                      id="user-user-name-in-certificate"
                      label={messages.siteSettingsUserUseNameInCertificate}
                      style={{ marginTop: 15 }}
                    />
                  </FormGroup>

                  <FormGroup label={messages.siteSettingsAccountEditing}>
                    <Checkbox
                      field="accountPreventUserEditingProfileInfo"
                      id="account-prevent-user-editing-profile-info"
                      label={messages.siteSettingsAccountEditingPrevent}
                    />
                  </FormGroup>

                  <FormGroup label={messages.usersGuestAccount}>
                    <Checkbox
                      field="accountGuestUseName"
                      id="account-guest-use-name"
                      label={messages.siteSettingsAccountGuestUseName}
                    />

                    <Checkbox
                      field="accountGuestEnableHomePage"
                      id="account-guest-enable-home-page"
                      label={messages.siteSettingsAccountGuestEnableHomePage}
                      labelTooltip="This also allows guest users to sign in from the main login page"
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>

                  <FormGroup label={messages.usersAccountsWithoutEmail}>
                    <Checkbox
                      field="accountEnableUsernameAccounts"
                      id="enable-username-accounts"
                      label={messages.siteSettingsEnableUsernameRegistration}
                    />
                    <Checkbox
                      field="accountLimitUsernameCreationToSuperadmins"
                      id="limit-username-creation-to-superadmins"
                      label={
                        messages.settingsAccountLimitUsernameCreationToSuperadmins
                      }
                      style={{ marginTop: 5 }}
                    />
                    <Checkbox
                      field="useAlternativeSetPassword"
                      id="use-alternative-set-password"
                      label={messages.settingsUseAlternativeSetPassword}
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>
                </>
              ) : null}

              {isVuoadmin ||
              serverConfig.registerEnableSettingsForSuperadmin ? (
                <>
                  <FormGroup label={messages.commonRegistration}>
                    <Checkbox
                      field="enableRegistration"
                      id="enable-registration"
                      label={messages.siteSettingsEnableRegistration}
                    />
                  </FormGroup>

                  <FormGroup label="Restrict registration type">
                    <Select
                      disabled={
                        !(
                          (formState.values.accountEnableUsernameAccounts ||
                            (formState.values.accountEnableUsernameAccounts ===
                              undefined &&
                              serverConfig.accountEnableUsernameAccounts)) &&
                          formState.values.enableRegistration
                        )
                      }
                      field="enableRegistrationType"
                      id="enable-registration-type"
                      options={[
                        {
                          label: messages.createAccountRestrictionNone,
                          value: 'any'
                        },
                        {
                          label: messages.createAccountRestrictionEmail,
                          value: 'email'
                        },
                        {
                          label: messages.createAccountRestrictionUsername,
                          value: 'username'
                        }
                      ]}
                      style={{ maxWidth: 200 }}
                    />
                  </FormGroup>

                  <FormGroup
                    label={messages.siteSettingsRegisterRestrictDomains}
                  >
                    <Domains
                      field="registerRestrictDomains"
                      id="register-restrict-domains"
                    />
                  </FormGroup>

                  {isVuoadmin ? (
                    <FormGroup>
                      <Checkbox
                        field="registerEnableSettingsForSuperadmin"
                        id="register-enable-settings-for-superadmin"
                        label={'Enable registration settings for superadmin'}
                      />
                    </FormGroup>
                  ) : null}
                </>
              ) : null}
            </FormSection>

            {isVuoadmin ? (
              <FormSection title="Expiration and deletion">
                <FormGroup label={messages.siteSettingsAccountExpiration}>
                  <RadioGroup
                    field="accountExpiration"
                    options={[
                      {
                        id: `account-expiration-never`,
                        label: messages.commonNever,
                        value: 'never'
                      },
                      {
                        id: `account-expiration-after-last-login`,
                        label: messages.siteSettingsAccountExpirationLogin,
                        value: 'after-last-login'
                      },
                      {
                        id: `account-expiration-after-creation`,
                        label: messages.siteSettingsAccountExpirationCreated,
                        value: 'after-creation'
                      }
                    ]}
                  />
                </FormGroup>

                <NumberStepper
                  disabled={formState.values.accountExpiration === 'never'}
                  field="accountExpirationTimeInDays"
                  id="account-expiration-time-in-days"
                  label={messages.siteSettingsAccountExpirationTimeInDays}
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />

                <NumberStepper
                  field="accountExpirationDeleteBufferInDays"
                  id="account-expiration-buffer-in-days"
                  label={
                    messages.siteSettingsAccountExpirationDeleteBufferInDays
                  }
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />

                <Checkbox
                  disabled={formState.values.accountExpiration === 'never'}
                  field="accountExpirationIncludeAdmins"
                  id="account-expiration-include-admins"
                  label={messages.siteSettingsAccountExpirationIncludeAdmins}
                  style={{ marginTop: 10 }}
                />

                <FormGroup label={'Account expiration warning'}>
                  <Checkbox
                    disabled={
                      formState.values.accountExpiration !== 'after-last-login'
                    }
                    field="accountExpirationSendEmailWarning"
                    id="account-expiration-send-email-warning"
                    label={'Send email warning before account expires'}
                  />

                  <NumberStepper
                    disabled={
                      formState.values.accountExpiration !== 'after-last-login'
                    }
                    field="accountExpirationSendEmailWarningDays"
                    id="account-expiration-send-email-warning-days"
                    label={'Time in days before warning is sent'}
                    min={1}
                    stepValue={1}
                    style={{ marginTop: 10 }}
                  />
                </FormGroup>

                <FormGroup label="Force expiration date">
                  <Checkbox
                    field="accountAddForceExpirationDate"
                    id="account-add-force-expiration-date"
                    label={messages.siteSettingsAccountAddForceExpirationDate}
                  />

                  <NumberStepper
                    disabled={!formState.values.accountAddForceExpirationDate}
                    field="accountAddForceExpirationDateMaxDays"
                    id="account-add-force-expiration-date-max-days"
                    label={
                      messages.siteSettingsAccountAddForceExpirationDateMaxDays
                    }
                    min={1}
                    stepValue={1}
                    style={{ marginTop: 10 }}
                  />
                </FormGroup>

                <FormGroup label={messages.profileAccountDeletion}>
                  <Checkbox
                    field="accountAllowDeletion"
                    id="account-allow-deletion"
                    label={messages.siteSettingsAccountAllowDeletion}
                    onValueChange={handleAllowDeletionChange}
                  />

                  <Checkbox
                    disabled={formState.values.accountAllowDeletion}
                    field="disableAccountDeleteRequests"
                    id="disable-account-delete-requests"
                    label={messages.siteSettingsAccountDisableDeleteRequests}
                    onValueChange={handleDisableAccountDeleteRequestsChange}
                    style={{ marginTop: 5 }}
                  />

                  <Checkbox
                    disabled={formState.values.disableAccountDeleteRequests}
                    field="allowAdminToManageDeleteRequests"
                    id="allow-admin-to-manage-delete-requests"
                    label={'Allow admins to manage delete requests'}
                    style={{ marginTop: 5 }}
                  />

                  <Checkbox
                    field="accountAnonymiseOnDeletion"
                    id="account-anonymize-on-deletion"
                    label={messages.siteSettingsAccountAnonymiseOnDeletion}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>

                <FormGroup
                  label="Delete request email"
                  style={{ marginTop: 25 }}
                >
                  <Checkbox
                    disabled={formState.values.disableAccountDeleteRequests}
                    field="accountDeleteRequestEmailEnabled"
                    id="account-delete-request-email-enabled"
                    label={'Enable account delete request email'}
                  />
                </FormGroup>

                <FormGroup label="Delete request email recipient">
                  <Text
                    disabled={formState.values.disableAccountDeleteRequests}
                    field="accountDeleteRequestEmailRecipient"
                    placeholder="Add recipient"
                    style={{ width: 400 }}
                  />

                  <Checkbox
                    disabled={formState.values.disableAccountDeleteRequests}
                    field="accountDeleteRequestEmailRecipientPreventDashboardUpdates"
                    id="account-delete-request-email-recipient-prevent-dashboard-updates"
                    label={'Prevent updating recipient from dashboard'}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>

                <FormGroup label="Delete request email additional content">
                  <TextArea
                    disabled={formState.values.disableAccountDeleteRequests}
                    field="accountDeleteRequestEmailContent"
                    placeholder="Add content"
                  />
                </FormGroup>
              </FormSection>
            ) : null}
          </div>
        )}
      </SiteSettingsForm>
    </div>
  );
};

export default SiteSettingsAccount;
